import React, { useEffect } from "react";
import { Autocomplete, Box, CircularProgress, formHelperTextClasses } from "@mui/material";
import { RoadsideTextField } from "components/FormElements/FormElements.styled";
import { InputBaseProps } from "@mui/material/InputBase";
import { InputProps as StandardInputProps } from "@mui/material/Input/Input";
import * as yup from "yup";
import { debounce } from "utils";
import { searchForAddress, Suggestion } from "services/QASService";
import { useAppErrors } from "components/AppErrors";

export const schemaShape = {
    global_address_key: yup.string().required(),
    text: yup.string().required(),
    format: yup.string().required(),
};

interface AddressFieldProps {
    name: string;
    label: string;
    value: Suggestion | null;
    onChange?: StandardInputProps["onChange"];
    onBlur?: InputBaseProps["onBlur"];
    error?: boolean;
    helperText?: React.ReactNode;
    type?: "residential" | "postal";
}

export const AddressField: React.FC<AddressFieldProps> = ({
    name,
    label,
    value,
    onChange,
    onBlur,
    error,
    helperText,
    type,
}) => {
    const { raiseError } = useAppErrors();
    const [currentValue, setCurrentValue] = React.useState<Suggestion | null>(value || null);
    const [inputValue, setInputValue] = React.useState("");
    const [options, setOptions] = React.useState<readonly Suggestion[]>(value ? [value] : []);
    const [loading, setLoading] = React.useState(false);

    const setIncomplete = (text: string) => {
        setCurrentValue(
            text.length > 0
                ? {
                      global_address_key: "__incomplete",
                      text: text,
                      matched: [],
                      format: "__incomplete",
                  }
                : null,
        );
    };

    useEffect(() => {
        if (!onChange) {
            return;
        }
        const evt = {
            target: { name: name, value: currentValue },
        };
        // @ts-ignore
        onChange(evt!);
    }, [name, currentValue, onChange]);

    // TODO: Maybe extract QAS fetch logic from this component?
    // Or leave as is
    const fetchSuggestions = React.useMemo(
        () =>
            debounce(async (request: { input: string }) => {
                setLoading(true);
                // Pass in right dataset
                try {
                    const searchResponse = await searchForAddress(request.input, type);
                    if (searchResponse?.result.suggestions) {
                        setOptions(searchResponse.result.suggestions);
                    }
                } catch (error: any) {
                    // Maybe handle this in the QASService?
                    raiseError(error);
                }
                setLoading(false);
            }, 500),
        [type, raiseError],
    );

    React.useEffect(() => {
        if (inputValue === "" || inputValue === currentValue?.text) {
            return undefined;
        }

        fetchSuggestions({ input: inputValue });
    }, [currentValue, inputValue, fetchSuggestions]);

    return (
        <Autocomplete
            getOptionLabel={(option) => (typeof option === "string" ? option : option.text)}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions={false}
            value={currentValue}
            onChange={(_, newValue: string | Suggestion | null) => {
                if (typeof newValue === "string") {
                    setIncomplete(newValue);
                } else {
                    setCurrentValue(newValue);
                    if (newValue) {
                        setOptions([newValue]);
                    }
                }
            }}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            renderInput={(params) => (
                <RoadsideTextField
                    {...params}
                    name={name}
                    label={label}
                    error={error}
                    helperText={helperText}
                    fullWidth
                    onBlur={(e) => {
                        if (!currentValue || currentValue.text !== e.target.value) {
                            setIncomplete(e.target.value);
                        }
                        if (onBlur) {
                            onBlur(e);
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: loading ? <CircularProgress size={32} /> : params.InputProps.endAdornment,
                    }}
                    sx={{
                        mb: 0,
                        [`& .${formHelperTextClasses.root}`]: {
                            textAlign: "center",
                        },
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                return (
                    <li key={key} {...rest} style={{ fontSize: 18, lineHeight: "26px" }}>
                        {option.text}
                    </li>
                );
            }}
            noOptionsText={
                <Box component={"span"} sx={{ fontSize: 18, lineHeight: "26px" }}>
                    No results found
                </Box>
            }
            forcePopupIcon={false}
            popupIcon={null}
            openOnFocus={true}
            isOptionEqualToValue={(option, value) => option.text === value.text}
            freeSolo
        />
    );
};
